import { useEffect, useState } from 'react';
import NavbarNew from './components/NavbarNew';
import HeroNew from './components/HeroNew';
import ProblemNew from './components/ProblemNew';
import SolutionNew from './components/SolutionNew';
import USPsNew from './components/USPsNew';
import ROINew from './components/ROINew';
import FAQNew from './components/FAQNew';
import ContactFormNew from './components/ContactFormNew';
import CTANew from './components/CTANew';
import FooterNew from './components/FooterNew';
import { useLanguage } from '../../hooks/useLanguage';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const { language } = useLanguage();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target as HTMLElement;
            el.style.opacity = '1';
            el.style.transform = 'translateY(0)';
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.12 }
    );

    const elements = document.querySelectorAll('.fade-in-section');
    elements.forEach((el) => {
      const htmlEl = el as HTMLElement;
      const existingDelay = htmlEl.style.transitionDelay || '0ms';
      htmlEl.style.opacity = '0';
      htmlEl.style.transform = 'translateY(28px)';
      htmlEl.style.transition = `opacity 0.65s ease-out ${existingDelay}, transform 0.65s ease-out ${existingDelay}`;
      observer.observe(htmlEl);
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://teliq.one';

    const websiteSchema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": `${siteUrl}/#website`,
      "name": "TELIQone",
      "url": siteUrl,
      "description": "TELIQone bietet modulare KI-Plattform für Telcos mit vollständiger Prozessautomatisierung von Kundenanfragen bis Rechnungsverarbeitung. DSGVO-konform, Made in Germany. 50% schnellere Ticketbearbeitung, 20% OPEX-Reduktion.",
      "inLanguage": ["de", "en"],
      "publisher": { 
        "@type": "Organization", 
        "@id": `${siteUrl}/#organization`,
        "name": "omniIT GmbH" 
      }
    };

    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": `${siteUrl}/#organization`,
      "name": "omniIT GmbH",
      "url": siteUrl,
      "logo": `${siteUrl}/vite.svg`,
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+49-89-998-2419-0",
        "email": "info@omniit.de",
        "contactType": "customer service",
        "areaServed": "DE",
        "availableLanguage": ["German", "English"]
      },
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Grünwalder Weg 28b",
        "addressLocality": "Oberhaching",
        "postalCode": "82041",
        "addressCountry": "DE"
      },
      "sameAs": ["https://www.linkedin.com/company/omniit-gmbh/"]
    };

    const softwareSchema = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "TELIQone KI-Prozessautomatisierung",
      "url": siteUrl,
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Cloud",
      "description": "Modulare KI-Plattform für Telekommunikationsanbieter mit vollständiger Prozessautomatisierung von Kundenanfragen bis Rechnungsverarbeitung. DSGVO-konform, Made in Germany.",
      "offers": {
        "@type": "Offer",
        "priceCurrency": "EUR",
        "availability": "https://schema.org/InStock"
      },
      "creator": {
        "@type": "Organization",
        "@id": `${siteUrl}/#organization`
      },
      "featureList": [
        "KI-Automatisierung Telco",
        "Prozessautomatisierung Telekommunikation",
        "DSGVO-konforme KI",
        "50% schnellere Ticketbearbeitung",
        "20% OPEX-Reduktion"
      ]
    };

    const webPageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${siteUrl}/#webpage`,
      "name": "TELIQone KI-Prozessautomatisierung für Telcos",
      "url": siteUrl,
      "description": "TELIQone bietet modulare KI-Plattform für Telcos mit vollständiger Prozessautomatisierung von Kundenanfragen bis Rechnungsverarbeitung. DSGVO-konform, Made in Germany. 50% schnellere Ticketbearbeitung, 20% OPEX-Reduktion.",
      "inLanguage": ["de", "en"],
      "dateModified": new Date().toISOString(),
      "isPartOf": {
        "@type": "WebSite",
        "@id": `${siteUrl}/#website`
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": siteUrl
          }
        ]
      }
    };

    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Wie lange dauert die Implementierung?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Ein typisches TELIQone-Rollout dauert 6–10 Wochen vom Kickoff bis zum Go-live. Dies umfasst Systemintegration, Prozess-Mapping, Feinabstimmung der Modelle auf Ihre Telco-Daten sowie Agentenschulung. Wir stellen Ihnen während der gesamten Implementierung ein dediziertes Team zur Seite."
          }
        },
        {
          "@type": "Question",
          "name": "Mit welchen Systemen integriert sich TELIQone?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "TELIQone verbindet sich nativ mit führenden BSS/OSS-Plattformen (z. B. Amdocs, Comverse, Ericsson), CRM-Systemen (Salesforce, SAP), Ticketing-Tools (ServiceNow, Jira) sowie jeder REST/SOAP-API. Individuelle Konnektoren sind auf Anfrage erhältlich."
          }
        },
        {
          "@type": "Question",
          "name": "Müssen wir unsere bestehende Infrastruktur ersetzen?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Nein. TELIQone ist als nicht-disruptive Overlay-Lösung konzipiert. Es legt sich über Ihren bestehenden Stack und erweitert ihn — kein Rip-and-Replace erforderlich. Ihre aktuellen Tools bleiben unverändert bestehen."
          }
        },
        {
          "@type": "Question",
          "name": "Wie sieht der Onboarding-Prozess aus?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Wir beginnen mit einer 2-wöchigen Discovery-Phase zur Erfassung Ihrer Prozesse und Datenflüsse. Anschließend konfigurieren und optimieren wir die Modelle, führen einen Pilot mit einem Teil Ihres Teams durch und skalieren von dort aus. Sie haben ab Tag eins einen festen Customer Success Manager."
          }
        },
        {
          "@type": "Question",
          "name": "Wo werden unsere Daten verarbeitet und gespeichert?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Alle Daten werden ausschließlich in deutschen Rechenzentren (ISO 27001 zertifiziert) verarbeitet und gespeichert. TELIQone ist vollständig DSGVO-konform, und die Datenhoheit verbleibt jederzeit bei Ihnen — wir verwenden Ihre Daten niemals zum Training gemeinsamer Modelle."
          }
        },
        {
          "@type": "Question",
          "name": "Ist TELIQone DSGVO-konform?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Ja. TELIQone wurde von Grund auf für die DSGVO-Konformität entwickelt. Wir bieten einen Auftragsverarbeitungsvertrag (AVV), unterstützen Betroffenenanfragen und führen vollständige Audit-Logs. Unser Rechtsteam ist in Deutschland ansässig."
          }
        },
        {
          "@type": "Question",
          "name": "Wie wird der Zugriff auf sensible Kundendaten kontrolliert?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "TELIQone verwendet rollenbasierte Zugriffskontrolle (RBAC), Ende-zu-Ende-Verschlüsselung (TLS 1.3 bei der Übertragung, AES-256 im Ruhezustand) sowie detaillierte Audit-Trails. SSO und MFA werden standardmäßig unterstützt."
          }
        },
        {
          "@type": "Question",
          "name": "Hat TELIQone unabhängige Sicherheitsaudits durchlaufen?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Ja. Wir führen jährliche Penetrationstests durch akkreditierte Drittunternehmen durch und halten die ISO 27001-Zertifizierung aufrecht. Auditberichte sind für Enterprise-Kunden unter NDA verfügbar."
          }
        },
        {
          "@type": "Question",
          "name": "Wie wird TELIQone berechnet?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "TELIQone wird auf Basis eines modularen SaaS-Abonnements berechnet — Sie zahlen für die aktivierten Module und das verarbeitete Volumen. Es gibt keine versteckten Einrichtungsgebühren. Kontaktieren Sie uns für ein maßgeschneidertes Angebot basierend auf Ihrer Agentenanzahl und Ihrem Ticketvolumen."
          }
        },
        {
          "@type": "Question",
          "name": "Gibt es eine Mindestvertragslaufzeit?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Unsere Standardverträge laufen 12 Monate, mit Optionen für 24 oder 36 Monate zu Vorzugskonditionen. Wir bieten auch ein 90-tägiges Pilotprogramm für neue Kunden an, die den ROI vor einer Verpflichtung validieren möchten."
          }
        },
        {
          "@type": "Question",
          "name": "Was ist im Abonnement enthalten?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Alle Abonnements beinhalten Plattformzugang, Modell-Updates, dedizierten Support, SLA-Garantien und Zugang zu unserem Customer-Success-Team. Enterprise-Pläne umfassen zusätzlich individuelle Integrationen, Vor-Ort-Schulungen und einen festen Account Manager."
          }
        },
        {
          "@type": "Question",
          "name": "Können wir mit einem Modul starten und später erweitern?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Absolut. TELIQone ist von Grund auf modular aufgebaut. Die meisten Kunden starten mit Ticket Intelligence oder Process Automation und erweitern auf weitere Module, sobald sie Ergebnisse sehen — ohne jegliche Systemmigration."
          }
        }
      ]
    };

    const scripts: HTMLScriptElement[] = [];
    [websiteSchema, organizationSchema, softwareSchema, webPageSchema, faqSchema].forEach((schema) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.text = JSON.stringify(schema);
      document.head.appendChild(s);
      scripts.push(s);
    });

    return () => {
      scripts.forEach((s) => {
        if (s.parentNode) document.head.removeChild(s);
      });
    };
  }, []);

  useEffect(() => {
    const metaContent = {
      de: {
        title: 'TELIQone KI-Prozessautomatisierung für Telcos',
        description: 'TELIQone bietet modulare KI-Plattform für Telcos mit vollständiger Prozessautomatisierung von Kundenanfragen bis Rechnungsverarbeitung. DSGVO-konform, Made in Germany. 50% schnellere Ticketbearbeitung, 20% OPEX-Reduktion.',
        locale: 'de_DE'
      },
      en: {
        title: 'TELIQone AI Process Automation for Telcos',
        description: 'TELIQone offers a modular AI platform for telcos with complete process automation from customer inquiries to invoice processing. GDPR-compliant, Made in Germany. 50% faster ticket processing, 20% OPEX reduction.',
        locale: 'en_US'
      }
    };

    const content = metaContent[language];

    // Update page title
    document.title = content.title;

    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', content.description);
    }

    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', content.title);
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', content.description);
    }

    const ogLocale = document.querySelector('meta[property="og:locale"]');
    if (ogLocale) {
      ogLocale.setAttribute('content', content.locale);
    }

    // Update Twitter Card tags
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', content.title);
    }

    const twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute('content', content.description);
    }

    // Cleanup: restore German defaults on unmount
    return () => {
      document.title = metaContent.de.title;
      if (metaDescription) {
        metaDescription.setAttribute('content', metaContent.de.description);
      }
      if (ogTitle) {
        ogTitle.setAttribute('content', metaContent.de.title);
      }
      if (ogDescription) {
        ogDescription.setAttribute('content', metaContent.de.description);
      }
      if (ogLocale) {
        ogLocale.setAttribute('content', metaContent.de.locale);
      }
      if (twitterTitle) {
        twitterTitle.setAttribute('content', metaContent.de.title);
      }
      if (twitterDescription) {
        twitterDescription.setAttribute('content', metaContent.de.description);
      }
    };
  }, [language]);

  return (
    <div className="min-h-screen bg-white">
      <NavbarNew scrolled={scrolled} />
      <HeroNew />
      <ProblemNew />
      <SolutionNew />
      <USPsNew />
      <ROINew />
      <FAQNew />
      <ContactFormNew />
      <CTANew />
      <FooterNew />
    </div>
  );
}